import { create } from 'evx'
import 'element-closest'
import * as FastClick from 'fastclick';
import Queue from './lib/Queue'
import Helpers from './lib/Helpers'

const { detect } = require('detect-browser');

import MethodCall from './components/MethodCall'
import MegaMenu from './components/MegaMenu'
import ClassChange from './components/ClassChange'
import SlideToggle from './components/SlideToggle'
import AddToCart from './components/AddToCart'
import Product from './components/Product'
import NumberSelector from './components/NumberSelector'
import Cart from './components/Cart'
import StickyElem from './components/StickyElem'
import LoadMore from './components/LoadMore'
import LockScroll from './components/LockScroll'
import ObjectFit from './components/ObjectFit'
import SideCart from './components/SideCart'
import Quicklink from './components/Quicklink'
import AjaxSearch from './components/AjaxSearch'
import SetCSSVar from './components/SetCSSVar'
import LazyLoad from './components/LazyLoad'
import LazyLoadTrigger from './components/LazyLoadTrigger'
import KeenSlider from './components/KeenSlider'
import CountryProvinceSelector from './components/CountryProvinceSelector'
import SlideToggleGroup from './components/SlideToggleGroup'
import Header from './components/Header'
import SlidingMenu from './components/SlidingMenu'
import Reveal from './components/Reveal'
import ScrollTo from './components/ScrollTo'
import CollectionFilters from './components/CollectionFilters'
import WhenScrolledPast from './components/WhenScrolledPast'
import RangeSlider from './components/RangeSlider'
import SetHTML from './components/SetHTML';
import ImageZoom from './components/ImageZoom';
import ReferenceImages from './components/ReferenceImages';
import RecentlyViewed from './components/RecentlyViewed';
import ReadMore from './components/ReadMore';
import CollectionClusters from './components/CollectionClusters';
import DeliveryDatePicker from './components/DeliveryDatePicker';
import KonvaColourChange from './components/KonvaColourChange';
import DynamicColourPicker from './components/DynamicColourPicker';

const components = {
  'lazy-load': LazyLoad,
  'lazy-load-trigger': LazyLoadTrigger,
  'header': Header,
  'set-css-var': SetCSSVar,
  'quicklink': Quicklink,
  'keen-slider': KeenSlider,
  'product': Product,
  'method-call': MethodCall,
  'image-zoom': ImageZoom,
  'mega-menus': MegaMenu,
  'class-change': ClassChange,
  'slide-toggle': SlideToggle,
  'toggle-group': SlideToggleGroup,
  'add-to-cart': AddToCart,
  'number-selector': NumberSelector,
  'cart': Cart,
  'sticky': StickyElem,
  'load-more': LoadMore,
  'lock-scroll': LockScroll, 
  'object-fit': ObjectFit,
  'side-cart': SideCart,
  'ajax-search': AjaxSearch,
  'country-province-selector': CountryProvinceSelector,
  'sliding-menu': SlidingMenu,
  'reveal': Reveal,
  'scroll-to': ScrollTo,
  'collection-filters': CollectionFilters,
  'when-scrolled-past': WhenScrolledPast,
  'set-html': SetHTML,
  'read-more': ReadMore,
  'range-slider': RangeSlider,
  'recently-viewed': RecentlyViewed,
  'reference-images': ReferenceImages,
  'collection-clusters': CollectionClusters,
  'delivery-date-picker': DeliveryDatePicker,
  'konva-colour-change': KonvaColourChange,
  'dynamic-colour-picker': DynamicColourPicker
}

const options = {
  turbolinks: false
}

const state = {
  eventQueue: new Queue(),
  ajaxCache: []
}

export default class Theme {
  constructor (ctx = state, passedOptions = {}) {
    this._options = {...passedOptions, ...options}
    let themeInfoElem = document.querySelector('[data-theme-information]')
    if (themeInfoElem) {
      ctx = {...ctx, ...JSON.parse(themeInfoElem.innerHTML)}
    }
    this._ctx = create(ctx)
    this._viewportEvent()
    this._bodyScrollListen()
    this._runHooks()
    this._browserDetect()
    this._setGorgiasText()
    this._components = []
  }

  _browserDetect () {
    const browser = detect()
    if (browser) {
      document.body.classList.add(`browser--${Helpers.handleize(browser.name)}`)
      document.body.classList.add(`os--${Helpers.handleize(browser.os)}`)
    }
  }

  _viewportEvent () {
    window.addEventListener('resize', () => {
      this._viewportLogic()
    }, { passive: true })

    document.addEventListener('DOMContentLoaded', () => {
      this._viewportLogic()
    })

    this._viewportLogic()
  }

  _bodyScrollListen () {
    const targetNode = document.body
    const config = { attributes: true, childList: false, subtree: false };

    const callback = function(mutationsList, observer) {
      document.documentElement.style.setProperty('--scroll-gap-right',getComputedStyle(document.body).paddingRight)
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
  }

  _viewportLogic () {
    let vh = window.innerHeight * 0.01;
    let vw = document.body.clientWidth * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--vw', `${vw}px`);
  }

  mountComponents (container = document) {
    for (let componentKey in components) {
      try {
        if (components.hasOwnProperty(componentKey)) {
          let selector = `[data-${componentKey}]`
          let elements = container.querySelectorAll(selector)
          for (let elem of elements) {
            this.mountComponent(componentKey, componentKey, elem)
          }
        }
      } catch (e) {
        console.error(e)
      }
    }
    window.__Theme = this
    window.__ThemeComponents = this._components

    this._ctx.emit('ffc--mounted')
  }

  unmountComponents (container = document) {
    for (var i = this._components.length - 1; i >= 0; i--) {
      let component = this._components[i]

      if (container.contains(component.elem)) {
        let componentObj = component.component
        if (typeof componentObj.unmount === 'function') {
          componentObj.unmount()
        }
        component = null
        componentObj = null
        this._components.splice(i, 1)
      }
    }
  }

  mountComponent (type, dataAttr, elem) {
    try {
      let optionStr = elem.getAttribute(`data-${dataAttr}`).trim()
      let options = {}
      if (optionStr.startsWith('{') || optionStr.startsWith('[')) {
        options = JSON.parse(optionStr)
      }
      let compInstance = new components[type](elem, this, options, this._ctx)
      this._components.push({
        type: type,
        elem: elem,
        id: compInstance._options.id,
        component: compInstance
      })
      compInstance.mount()
    } catch(e) {
      console.error(`-----ERROR IN COMPONENT----- \ntype: ${type}\nelement and stack trace below:`)
      console.error(elem)
      console.error(e)
    }
  }

  getOptions () {
    return this._options
  }

  getComponent (id) {
    return this._components.find(component => component.id === id)
  }

  getComponentByElem (elem) {
    return this._components.find(component => component.elem === elem)
  }

  _runHooks () {
    if ('addEventListener' in document) {
      document.addEventListener('DOMContentLoaded', function() {
        FastClick.attach(document.body);
      }, false);
    }
    this._ctx.on('cart-item-added', state => {
      if (window.pintrk) {
        pintrk('track', 'addtocart', {
          value: state.lastItemAdded.item.price / 100.00,
          order_quantity: state.lastItemAdded.quantity 
        })
      }
      if (window.fbq) {
        fbq('track', 'AddToCart')
      }
    })
  }

  async _setGorgiasText () {
    var initGorgiasChatPromise = (window.GorgiasChat) ? window.GorgiasChat.init() : new Promise(function (resolve) { window.addEventListener('gorgias-widget-loaded', function () { resolve();})});

    await initGorgiasChatPromise
    
    let sspDict = {
      "signIn": "Sign in",
      "signInDifferentAccount": "Sign in to a different account",
      "signInToAccessOrders": "To continue, please sign in to access your orders.",
      "signInWithOrderNumber": "Sign In With Order Number",
      "yourEmail": "Enter the email address associated with your order to retrieve your order details"
    }

    GorgiasChat.updateSSPTexts(sspDict)

    var timer = setInterval(function () {
      var chatWindowHead = document.querySelector('#gorgias-chat-container')?.querySelector('#chat-window')?.contentWindow.document.querySelector('head');
      if (![...chatWindowHead?.children].some(x => x.getAttribute("data-emotion"))) {
          return;
      }
      clearInterval(timer);

      var chatStyle = document.createElement('style');
      chatStyle.textContent = `
        #self-service-portal-container form + span button{
          color: #6a6a6a !important;
        }
        #self-service-portal-container form + span + div button{
          background: #000 !important;
          color: #fff !important;
        }
      `;
      chatWindowHead.appendChild(chatStyle);
    }, 50);
  }
}